import React, { useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Col,
  Divider,
  Form,
  Input,
  Modal,
  Row,
  Select,
  Space,
  Table,
  Tabs,
  Tag,
  Typography,
  message,
} from 'antd';
import {
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
  SafetyCertificateOutlined,
  TeamOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import {
  PlatformRoleCatalogItem,
} from '@erpgold/shared';
import { DataTable } from '../../components';
import { usePlatformUsers } from '../../hooks/usePlatformUsers';
import type {
  CreatePlatformUserDto,
  PlatformAccount,
  PlatformAccountRole,
  PlatformAccountStatus,
} from '../../services/platform-user.service';

const { Title, Text } = Typography;
const SUPER_ADMIN_ROLE = 'super_admin';
const SUPPORT_ADMIN_ROLE = 'support_admin';
const BILLING_ADMIN_ROLE = 'billing_admin';
const STATUS_ACTIVE: PlatformAccountStatus = 'active';
const STATUS_INACTIVE: PlatformAccountStatus = 'inactive';
const STATUS_LOCKED: PlatformAccountStatus = 'locked';

type UserFormValues = {
  email: string;
  name: string;
  role: PlatformAccountRole;
  status: PlatformAccountStatus;
  password?: string;
  permissions?: string[];
};

type RoleFormValues = {
  key: string;
  label: string;
  description?: string;
  default_permissions?: string[];
};

const roleTagColor: Record<string, string> = {
  [SUPER_ADMIN_ROLE]: 'red',
  [SUPPORT_ADMIN_ROLE]: 'blue',
  [BILLING_ADMIN_ROLE]: 'gold',
};

const statusTagColor: Record<PlatformAccountStatus, string> = {
  [STATUS_ACTIVE]: 'success',
  [STATUS_INACTIVE]: 'default',
  [STATUS_LOCKED]: 'error',
};

const roleLabelMap: Record<string, string> = {
  [SUPER_ADMIN_ROLE]: 'Super Admin',
  [SUPPORT_ADMIN_ROLE]: 'Hỗ trợ',
  [BILLING_ADMIN_ROLE]: 'Thanh toán',
};

const statusLabelMap: Record<PlatformAccountStatus, string> = {
  [STATUS_ACTIVE]: 'Hoạt động',
  [STATUS_INACTIVE]: 'Tạm dừng',
  [STATUS_LOCKED]: 'Đã khoá',
};

const getErrorMessage = (error: unknown, fallback: string): string => {
  const maybe = error as { response?: { data?: { message?: string } }; message?: string };
  return maybe?.response?.data?.message || maybe?.message || fallback;
};

export const UserAccessManagement: React.FC = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<PlatformAccount | null>(null);
  const [form] = Form.useForm<UserFormValues>();
  const [isRoleModalOpen, setIsRoleModalOpen] = useState(false);
  const [editingRole, setEditingRole] = useState<PlatformRoleCatalogItem | null>(null);
  const [roleForm] = Form.useForm<RoleFormValues>();

  const {
    users,
    loading,
    error,
    meta,
    filters,
    setFilters,
    roleCatalog,
    permissionCatalog,
    refetch,
    createUser,
    updateUser,
    deleteUser,
    createRole,
    updateRole,
    deleteRole,
  } = usePlatformUsers();

  const roleCatalogMap = useMemo(
    () => new Map(roleCatalog.map((role) => [role.key, role])),
    [roleCatalog],
  );

  const permissionOptions = useMemo(
    () =>
      permissionCatalog.flatMap((group) =>
        group.permissions.map((permission) => ({
          value: permission.key,
          label: `${group.label} - ${permission.label}`,
        })),
      ),
    [permissionCatalog],
  );

  const openCreateModal = () => {
    setEditingUser(null);
    form.resetFields();
    setIsModalOpen(true);
  };

  const openEditModal = (record: PlatformAccount) => {
    setEditingUser(record);
    form.setFieldsValue({
      email: record.email,
      name: record.name,
      role: record.role,
      status: record.status,
      permissions: record.permissions,
    });
    setIsModalOpen(true);
  };

  const handleDelete = (record: PlatformAccount) => {
    Modal.confirm({
      title: 'Xoá tài khoản quản trị?',
      content: `Bạn có chắc chắn muốn xoá tài khoản ${record.email}?`,
      okType: 'danger',
      okText: 'Xoá',
      cancelText: 'Huỷ',
      onOk: async () => {
        try {
          await deleteUser(record.id);
          message.success('Đã xoá tài khoản');
        } catch (err) {
          message.error(getErrorMessage(err, 'Không thể xoá tài khoản'));
        }
      },
    });
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      if (editingUser) {
        await updateUser(editingUser.id, {
          email: values.email,
          name: values.name,
          role: values.role,
          status: values.status,
          permissions: values.permissions,
          password: values.password || undefined,
        });
        message.success('Đã cập nhật tài khoản');
      } else {
        const payload: CreatePlatformUserDto = {
          email: values.email,
          name: values.name,
          role: values.role,
          status: values.status,
          permissions: values.permissions,
          password: values.password,
        };
        await createUser(payload);
        message.success('Đã tạo tài khoản');
      }

      setIsModalOpen(false);
      setEditingUser(null);
      form.resetFields();
    } catch (err) {
      if (err instanceof Error) {
        message.error(getErrorMessage(err, 'Lưu tài khoản thất bại'));
      }
    }
  };

  const openCreateRoleModal = () => {
    setEditingRole(null);
    roleForm.resetFields();
    setIsRoleModalOpen(true);
  };

  const openEditRoleModal = (role: PlatformRoleCatalogItem) => {
    setEditingRole(role);
    roleForm.setFieldsValue({
      key: role.key,
      label: role.label,
      description: role.description,
      default_permissions: role.default_permissions,
    });
    setIsRoleModalOpen(true);
  };

  const handleDeleteRole = (role: PlatformRoleCatalogItem) => {
    if (!role.id) {
      message.error('Không xác định được vai trò để xoá');
      return;
    }

    if (role.is_system) {
      message.warning('Vai trò hệ thống không thể xoá');
      return;
    }

    Modal.confirm({
      title: 'Xoá vai trò?',
      content: `Bạn có chắc chắn muốn xoá vai trò ${role.label}?`,
      okType: 'danger',
      okText: 'Xoá',
      cancelText: 'Huỷ',
      onOk: async () => {
        try {
          await deleteRole(role.id as string);
          message.success('Đã xoá vai trò');
        } catch (err) {
          message.error(getErrorMessage(err, 'Không thể xoá vai trò'));
        }
      },
    });
  };

  const handleSaveRole = async () => {
    try {
      const values = await roleForm.validateFields();
      if (editingRole?.id) {
        await updateRole(editingRole.id, {
          label: values.label,
          description: values.description,
          default_permissions: values.default_permissions,
        });
        message.success('Đã cập nhật vai trò');
      } else {
        await createRole({
          key: values.key,
          label: values.label,
          description: values.description,
          default_permissions: values.default_permissions,
        });
        message.success('Đã tạo vai trò');
      }

      setIsRoleModalOpen(false);
      setEditingRole(null);
      roleForm.resetFields();
    } catch (err) {
      if (err instanceof Error) {
        message.error(getErrorMessage(err, 'Lưu vai trò thất bại'));
      }
    }
  };

  const accountColumns: ColumnsType<PlatformAccount> = [
    {
      title: 'Tài khoản',
      key: 'account',
      render: (_, record) => (
        <Space direction="vertical" size={0}>
          <Text strong>{record.name}</Text>
          <Text type="secondary" style={{ fontSize: 12 }}>{record.email}</Text>
        </Space>
      ),
    },
    {
      title: 'Vai trò',
      dataIndex: 'role',
      key: 'role',
      width: 160,
      render: (role: PlatformAccountRole) => {
        const roleMeta = roleCatalogMap.get(role);
        return (
          <Tag color={roleTagColor[role] || 'default'}>
            {roleMeta?.label || roleLabelMap[role] || role}
          </Tag>
        );
      },
    },
    {
      title: 'Trạng thái',
      dataIndex: 'status',
      key: 'status',
      width: 130,
      render: (status: PlatformAccountStatus) => (
        <Tag color={statusTagColor[status]}>{statusLabelMap[status]}</Tag>
      ),
    },
    {
      title: 'Số quyền',
      key: 'permissions',
      width: 110,
      render: (_, record) => <Tag>{record.permissions.includes('*') ? 'All' : record.permissions.length}</Tag>,
    },
    {
      title: 'Lần đăng nhập gần nhất',
      dataIndex: 'last_login_at',
      key: 'last_login_at',
      width: 180,
      render: (value?: string | null) => value || 'Chưa có dữ liệu',
    },
  ];

  const roleColumns: ColumnsType<(typeof roleCatalog)[number]> = [
    {
      title: 'Vai trò',
      dataIndex: 'label',
      key: 'label',
      width: 220,
      render: (label, record) => (
        <Space direction="vertical" size={0}>
          <Text strong>{label}</Text>
          <Text type="secondary" style={{ fontSize: 12 }}>{record.key}</Text>
        </Space>
      ),
    },
    {
      title: 'Mô tả',
      dataIndex: 'description',
      key: 'description',
    },
    {
      title: 'Quyền mặc định',
      dataIndex: 'default_permissions',
      key: 'default_permissions',
      width: 220,
      render: (permissions: string[]) => (
        <Text>{permissions.includes('*') ? 'Toàn quyền (*)' : `${permissions.length} quyền`}</Text>
      ),
    },
    {
      title: 'Loại',
      key: 'is_system',
      width: 120,
      render: (_, record) => (
        <Tag color={record.is_system ? 'processing' : 'default'}>
          {record.is_system ? 'Hệ thống' : 'Tùy chỉnh'}
        </Tag>
      ),
    },
    {
      title: 'Thao tác',
      key: 'actions',
      width: 140,
      render: (_, record) => (
        <Space>
          <Button size="small" icon={<EditOutlined />} onClick={() => openEditRoleModal(record)}>
            Sửa
          </Button>
          <Button
            size="small"
            danger
            icon={<DeleteOutlined />}
            disabled={record.is_system}
            onClick={() => handleDeleteRole(record)}
          >
            Xoá
          </Button>
        </Space>
      ),
    },
  ];

  const filterContent = (
    <Row gutter={16}>
      <Col span={8}>
        <Select
          allowClear
          placeholder="Lọc theo vai trò"
          value={filters.role}
          style={{ width: '100%' }}
          onChange={(value) => setFilters({ ...filters, role: value, page: 1 })}
          options={roleCatalog.map((role) => ({ value: role.key, label: role.label }))}
        />
      </Col>
      <Col span={8}>
        <Select
          allowClear
          placeholder="Lọc theo trạng thái"
          value={filters.status}
          style={{ width: '100%' }}
          onChange={(value) => setFilters({ ...filters, status: value, page: 1 })}
          options={[
            { value: STATUS_ACTIVE, label: 'Hoạt động' },
            { value: STATUS_INACTIVE, label: 'Tạm dừng' },
            { value: STATUS_LOCKED, label: 'Đã khoá' },
          ]}
        />
      </Col>
    </Row>
  );

  return (
    <div>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Title level={4} style={{ margin: 0 }}>
            Quản lý tài khoản và phân quyền
          </Title>
        </Col>
      </Row>

      {error && (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
          message="Không thể tải dữ liệu tài khoản"
          description={error.message}
          action={<Button onClick={refetch}>Thử lại</Button>}
        />
      )}

      <Tabs
        items={[
          {
            key: 'accounts',
            label: (
              <Space>
                <TeamOutlined />
                Tài khoản quản trị
              </Space>
            ),
            children: (
              <DataTable<PlatformAccount>
                title={`Danh sách tài khoản (${meta.total})`}
                data={users}
                columns={accountColumns}
                loading={loading}
                rowKey="id"
                searchable
                searchPlaceholder="Tìm theo email hoặc tên"
                onSearch={(value) => setFilters({ ...filters, search: value, page: 1 })}
                onRefresh={refetch}
                filters={filterContent}
                pagination={{
                  current: meta.page,
                  pageSize: meta.limit,
                  total: meta.total,
                  onChange: (page, pageSize) => setFilters({ ...filters, page, limit: pageSize }),
                }}
                extraActions={
                  <Button type="primary" icon={<PlusOutlined />} onClick={openCreateModal}>
                    Thêm tài khoản
                  </Button>
                }
                actionColumn={{
                  actions: (record) => [
                    {
                      key: 'edit',
                      label: 'Chỉnh sửa',
                      icon: <EditOutlined />,
                      onClick: () => openEditModal(record),
                    },
                    {
                      key: 'delete',
                      label: 'Xoá',
                      icon: <DeleteOutlined />,
                      danger: true,
                      onClick: () => handleDelete(record),
                    },
                  ],
                }}
              />
            ),
          },
          {
            key: 'roles',
            label: (
              <Space>
                <SafetyCertificateOutlined />
                Vai trò và quyền
              </Space>
            ),
            children: (
              <Space direction="vertical" size={16} style={{ width: '100%' }}>
                <Row justify="end">
                  <Button type="primary" icon={<PlusOutlined />} onClick={openCreateRoleModal}>
                    Thêm vai trò
                  </Button>
                </Row>
                <Table
                  rowKey={(record) => record.id || record.key}
                  dataSource={roleCatalog}
                  columns={roleColumns}
                  pagination={false}
                />
                <Divider style={{ margin: 0 }} />
                <Title level={5} style={{ margin: 0 }}>Danh mục quyền</Title>
                {permissionCatalog.map((group) => (
                  <div key={group.key}>
                    <Text strong>{group.label}</Text>
                    <div style={{ marginTop: 8 }}>
                      {group.permissions.map((permission) => (
                        <Tag key={permission.key} style={{ marginBottom: 8 }}>
                          {permission.key}
                        </Tag>
                      ))}
                    </div>
                  </div>
                ))}
              </Space>
            ),
          },
        ]}
      />

      <Modal
        open={isModalOpen}
        title={editingUser ? 'Cập nhật tài khoản' : 'Tạo tài khoản mới'}
        onCancel={() => {
          setIsModalOpen(false);
          setEditingUser(null);
          form.resetFields();
        }}
        onOk={handleSave}
        okText={editingUser ? 'Lưu' : 'Tạo'}
        cancelText="Huỷ"
        destroyOnClose
      >
        <Form<UserFormValues>
          form={form}
          layout="vertical"
          initialValues={{
            role: SUPPORT_ADMIN_ROLE,
            status: STATUS_ACTIVE,
            permissions: [],
          }}
        >
          <Form.Item
            name="name"
            label="Họ tên"
            rules={[{ required: true, message: 'Nhập họ tên' }]}
          >
            <Input placeholder="Nguyễn Văn A" />
          </Form.Item>

          <Form.Item
            name="email"
            label="Email"
            rules={[
              { required: true, message: 'Nhập email' },
              { type: 'email', message: 'Email không hợp lệ' },
            ]}
          >
            <Input placeholder="admin@erpgold.vn" />
          </Form.Item>

          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name="role" label="Vai trò" rules={[{ required: true }]}>
                <Select
                  options={roleCatalog.map((role) => ({
                    value: role.key,
                    label: role.label,
                  }))}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="status" label="Trạng thái" rules={[{ required: true }]}>
                <Select
                  options={[
                    { value: STATUS_ACTIVE, label: 'Hoạt động' },
                    { value: STATUS_INACTIVE, label: 'Tạm dừng' },
                    { value: STATUS_LOCKED, label: 'Đã khoá' },
                  ]}
                />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="permissions" label="Quyền truy cập">
            <Select
              mode="multiple"
              allowClear
              optionFilterProp="label"
              options={permissionOptions}
              placeholder="Chọn quyền theo nhu cầu"
            />
          </Form.Item>

          <Form.Item
            name="password"
            label={editingUser ? 'Mật khẩu mới (tuỳ chọn)' : 'Mật khẩu khởi tạo (tuỳ chọn)'}
            rules={[{ min: 8, message: 'Tối thiểu 8 ký tự' }]}
          >
            <Input.Password placeholder="Để trống để dùng mặc định hệ thống" />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        open={isRoleModalOpen}
        title={editingRole ? 'Cập nhật vai trò' : 'Tạo vai trò mới'}
        onCancel={() => {
          setIsRoleModalOpen(false);
          setEditingRole(null);
          roleForm.resetFields();
        }}
        onOk={handleSaveRole}
        okText={editingRole ? 'Lưu' : 'Tạo'}
        cancelText="Huỷ"
        destroyOnClose
      >
        <Form<RoleFormValues>
          form={roleForm}
          layout="vertical"
          initialValues={{
            default_permissions: [],
          }}
        >
          <Form.Item
            name="key"
            label="Mã vai trò"
            rules={[
              { required: true, message: 'Nhập mã vai trò' },
              {
                pattern: /^[a-z0-9_]+$/,
                message: 'Chỉ gồm chữ thường, số và dấu gạch dưới',
              },
            ]}
          >
            <Input placeholder="ops_admin" disabled={Boolean(editingRole)} />
          </Form.Item>

          <Form.Item
            name="label"
            label="Tên vai trò"
            rules={[{ required: true, message: 'Nhập tên vai trò' }]}
          >
            <Input placeholder="Quản trị vận hành" />
          </Form.Item>

          <Form.Item name="description" label="Mô tả">
            <Input.TextArea rows={3} placeholder="Mô tả phạm vi của vai trò" />
          </Form.Item>

          <Form.Item name="default_permissions" label="Quyền mặc định">
            <Select
              mode="multiple"
              allowClear
              optionFilterProp="label"
              options={permissionOptions}
              placeholder="Chọn quyền mặc định cho vai trò"
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default UserAccessManagement;
